import Container from "../../../components/element/container";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/element/icon";
import FullModal from "../../../components/modal/full.modal";
import { handleCatchError } from "../../../helpers/handle.catch.error";
import { showToast } from "../../../helpers/show.toast";
import { createIdentity } from "../../../redux/reducer/order.reducer";
import SubmittedMedia from "../../../components/common/submitted.media";

const states = [
  "Johor",
  "Kedah",
  "Kelantan",
  "Kuala Lumpur",
  "Labuan",
  "Melaka",
  "Negeri Sembilan",
  "Pahang",
  "Penang",
  "Perak",
  "Perlis",
  "Putrajaya",
  "Sabah",
  "Sarawak",
  "Selangor",
  "Terengganu",
];
const requiredMark = (
  <span style={{ color: "#ff3333" }} aria-hidden="true">
    {" "}
    *
  </span>
);

const individualIdentityFields = [
  {
    label: "IC No. / Passport Number",
    key: "ic_no",
    type: "number",
    placeholder: "e.g 012345678910",
    wide: true,
  },
  {
    label: "Legal First Name",
    key: "legal_first_name",
    placeholder: "e.g John",
  },
  {
    label: "Legal Last Name",
    key: "legal_last_name",
    placeholder: "e.g Doe",
  },
  {
    label: "Address / Unit No",
    key: "address",
    placeholder: "e.g 13, Jalan 3/31",
  },
  {
    label: "City",
    key: "city",
    placeholder: "e.g Klang",
  },
  {
    label: "Postcode",
    key: "postcode",
    type: "number",
    placeholder: "e.g 41000",
  },
  {
    label: "State",
    key: "state",
  },
];

const companyIdentityFields = [
  {
    label: "Company Name",
    key: "company_name",
    placeholder: "e.g DCAR Sdn Bhd",
  },
  {
    label: "Company Email",
    key: "company_email",
    placeholder: "e.g info@dcar.com",
  },
  {
    label: "Company Register No",
    key: "company_register_no",
    placeholder: "e.g 202301046619",
  },
  {
    label: "Tin",
    key: "tin",
    placeholder: "e.g 1540533-U",
  },
  {
    label: "Address / Unit No",
    key: "address",
    placeholder: "e.g 13, Jalan 3/31",
  },
  {
    label: "City",
    key: "city",
    placeholder: "e.g Klang",
  },
  {
    label: "Postcode",
    key: "postcode",
    type: "number",
    placeholder: "e.g 41000",
  },
  {
    label: "State",
    key: "state",
  },
];

const individualUploads = [
  ["Front of ID", "id_front"],
  ["Back of ID", "id_back"],
  ["Driving License Front", "license_front"],
  ["Driving License Back", "license_back"],
];

const companyUploads = [
  ["Company Full Set SSM", "company_ssm", "material-symbols:upload-rounded"],
  ["Front of Director ID", "id_front", "uil:camera"],
  ["Back of Director ID", "id_back", "uil:camera"],
  ["Front of Director Driving License", "license_front", "uil:camera"],
  ["Back of Director Driving License", "license_back", "uil:camera"],
];

const identityForm = {
  registration_type: "",
  buyer_type: "",

  ic_no: "",
  legal_first_name: "",
  legal_last_name: "",

  company_name: "",
  company_email: "",
  company_register_no: "",
  tin: "",

  address: "",
  city: "",
  postcode: "",
  state: "",

  company_ssm: null,
  id_front: null,
  id_back: null,
  license_front: null,
  license_back: null,
};

const identitySchema = Yup.object().shape({
  registration_type: Yup.string()
    .oneOf(["individual", "company"])
    .required("Registration type is required"),

  buyer_type: Yup.string()
    .oneOf(["cash", "loan"])
    .required("Please choose either Cash Buy or Apply Loan"),

  ic_no: Yup.string().when("registration_type", {
    is: "individual",
    then: (schema) =>
      schema.required("IC number or passport number is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  legal_first_name: Yup.string().when("registration_type", {
    is: "individual",
    then: (schema) => schema.required("Legal first name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  legal_last_name: Yup.string().when("registration_type", {
    is: "individual",
    then: (schema) => schema.required("Legal last name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  company_name: Yup.string().when("registration_type", {
    is: "company",
    then: (schema) => schema.required("Company name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  company_email: Yup.string().when("registration_type", {
    is: "company",
    then: (schema) =>
      schema
        .email("Please enter a valid company email")
        .required("Company email is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  company_register_no: Yup.string().when("registration_type", {
    is: "company",
    then: (schema) => schema.required("Company register number is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  tin: Yup.string().when("registration_type", {
    is: "company",
    then: (schema) => schema.required("TIN is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  address: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  postcode: Yup.string().required("Postcode is required"),
  state: Yup.string().required("State is required"),

  company_ssm: Yup.mixed()
    .nullable()
    .when("registration_type", {
      is: "company",
      then: (schema) => schema.required("Company Full Set SSM is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

  id_front: Yup.mixed().nullable().required("Front of ID is required"),

  id_back: Yup.mixed().nullable().required("Back of ID is required"),

  license_front: Yup.mixed()
    .nullable()
    .required("Driving license front is required"),

  license_back: Yup.mixed()
    .nullable()
    .required("Driving license back is required"),
});

export default function IdentityDetailsModal({
  orderId,
  onClose,
  onComplete,
  readOnly = false,
  initialData = {},
  documents = [],
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { createIdentityLoading } = useSelector((state) => state.common);

  const handleSubmit = (values, { setFieldError, setSubmitting }) => {
    if (readOnly) {
      setSubmitting(false);
      return;
    }
    const formData = new FormData();

    const commonKeys = [
      "registration_type",
      "buyer_type",
      "address",
      "city",
      "postcode",
      "state",
      "id_front",
      "id_back",
      "license_front",
      "license_back",
    ];

    const individualKeys = ["ic_no", "legal_first_name", "legal_last_name"];

    const companyKeys = [
      "company_name",
      "company_email",
      "company_register_no",
      "tin",
      "company_ssm",
    ];

    const submitKeys = [
      ...commonKeys,
      ...(values.registration_type === "company"
        ? companyKeys
        : individualKeys),
    ];

    submitKeys.forEach((key) => {
      const value = values[key];

      if (value !== null && value !== undefined && value !== "") {
        formData.append(key, value);
      }
    });

    dispatch(
      createIdentity({
        order_id: orderId,
        data: formData,
      }),
    )
      .unwrap()
      .then((response) => {
        showToast(
          "Identity details submitted!",
          "Your identity information has been submitted successfully.",
          "success",
        );

        onComplete?.(response, values);
      })
      .catch((error) => {
        handleCatchError(error, setFieldError);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <FullModal
      independent
      show={true}
      close={onClose}
      centered
      screenSize="xl"
      className="purchase-form-modal"
      dialogClassName="purchase-form-modal-dialog identity-modal-dialog"
      contentClassName="purchase-form-modal-content"
      content={
        <section
          className={`purchase-form-dialog identity-dialog ${readOnly ? "purchase-form-readonly" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="identity-details-title"
        >
          <header className="purchase-form-header">
            <button
              className="purchase-modal-back"
              type="button"
              onClick={onClose}
              aria-label="Back"
            >
              <Icon icon="ep:back" />
            </button>

            <h1
              className="_label purchase-form-title"
              id="identity-details-title"
            >
              {t("Identity Details")}
            </h1>
          </header>

          <p className="purchase-form-security security-note-inline">
            <Icon
              tag="span"
              icon="solar:lock-keyhole-linear"
              className="security-lock"
            />
            This information will be used for Sales Order and JPJ ownership
            transfer preparation.
          </p>

          <Formik
            initialValues={
              readOnly
                ? Object.fromEntries(
                    Object.entries(identityForm).map(([key, fallback]) => [
                      key,
                      initialData[key] ?? fallback,
                    ]),
                  )
                : identityForm
            }
            validationSchema={readOnly ? undefined : identitySchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              isSubmitting,
              setFieldTouched,
              setFieldValue,
              touched,
              values,
            }) => {
              const isCompany = values.registration_type === "company";

              const identityFields = isCompany
                ? companyIdentityFields
                : individualIdentityFields;

              const uploads = isCompany
                ? companyUploads
                : individualUploads.map(([label, key]) => [
                    label,
                    key,
                    "uil:camera",
                  ]);

              return (
                <Form className="purchase-form">
                  {readOnly && (
                    <p className="purchase-review-note">
                      Submitted · View only
                    </p>
                  )}
                  <fieldset disabled={readOnly}>
                    <legend>1. Registration Type{requiredMark}</legend>

                    <Container tag="div" className="registration-options">
                      {[
                        ["individual", "solar:user-linear", "Individual"],
                        ["company", "solar:buildings-2-linear", "Company"],
                      ].map(([value, icon, label]) => (
                        <button
                          type="button"
                          disabled={readOnly}
                          className={
                            values.registration_type === value ? "selected" : ""
                          }
                          onClick={() => {
                            setFieldValue("registration_type", value);

                            setFieldTouched("registration_type", true, false);
                          }}
                          key={value}
                        >
                          <Icon icon={icon} />
                          <span className="_label registration-option-title">
                            {label}
                          </span>
                        </button>
                      ))}
                    </Container>

                    {touched.registration_type && errors.registration_type && (
                      <Container tag="div" className="element _errors">
                        {t(errors.registration_type)}
                      </Container>
                    )}
                  </fieldset>

                  {(values.registration_type || readOnly) && (
                    <fieldset disabled={readOnly}>
                      <legend>2. Identity Type</legend>

                      <Container tag="div" className="purchase-field-grid">
                        {identityFields.map((field) => (
                          <label
                            className={field.wide ? "wide" : ""}
                            key={field.key}
                          >
                            <span>
                              {field.label}
                              {requiredMark}
                            </span>

                            {field.key === "state" ? (
                              <select
                                disabled={readOnly}
                                name="state"
                                value={values.state}
                                aria-required="true"
                                onChange={(event) =>
                                  setFieldValue("state", event.target.value)
                                }
                                onBlur={() => setFieldTouched("state", true)}
                              >
                                <option value="">
                                  {readOnly
                                    ? "Not provided"
                                    : t("Select state")}
                                </option>
                                {states.map((state) => (
                                  <option key={state} value={state}>
                                    {state}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                readOnly={readOnly}
                                aria-required="true"
                                name={field.key}
                                value={values[field.key]}
                                type={field.type}
                                onChange={(event) =>
                                  setFieldValue(field.key, event.target.value)
                                }
                                onBlur={() => setFieldTouched(field.key, true)}
                                placeholder={
                                  readOnly ? "Not provided" : field.placeholder
                                }
                              />
                            )}

                            {touched[field.key] && errors[field.key] && (
                              <Container tag="div" className="element _errors">
                                {t(errors[field.key])}
                              </Container>
                            )}
                          </label>
                        ))}

                        {uploads.map(([label, key, uploadIcon]) => (
                          <label className="purchase-upload" key={key}>
                            <span>
                              {label}
                              {requiredMark}
                            </span>

                            {readOnly ? (
                              <SubmittedMedia
                                document={documents.find(
                                  (document) => document.group === key,
                                )}
                                icon={uploadIcon}
                              />
                            ) : (
                              <>
                                <span
                                  className="purchase-upload-box"
                                  onDragOver={(event) => event.preventDefault()}
                                  onDrop={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    const file = event.dataTransfer.files?.[0];
                                    if (!file) return;
                                    setFieldTouched(key, true, false);
                                    setFieldValue(key, file);
                                  }}
                                >
                                  <Icon icon={uploadIcon} />

                                  <span className="purchase-upload-copy">
                                    <span className="_label purchase-upload-name">
                                      {values[key]?.name || "Upload File"}
                                    </span>

                                    {!values[key]?.name && (
                                      <small className="accept-file-label">
                                        {t("JPG, PNG or PDF")}
                                      </small>
                                    )}
                                  </span>
                                </span>

                                <input
                                  className="purchase-upload-input"
                                  type="file"
                                  name={key}
                                  accept="image/*,.pdf"
                                  onChange={(event) => {
                                    const file = event.currentTarget.files?.[0];
                                    if (!file) return;
                                    setFieldTouched(key, true, false);
                                    setFieldValue(key, file);
                                  }}
                                />
                              </>
                            )}

                            {touched[key] && errors[key] && (
                              <Container tag="div" className="element _errors">
                                {t(errors[key])}
                              </Container>
                            )}
                          </label>
                        ))}
                      </Container>
                    </fieldset>
                  )}

                  {(values.registration_type || readOnly) && (
                    <fieldset disabled={readOnly}>
                      <legend>3. Purchase method{requiredMark}</legend>

                      <p className="purchase-method-note">
                        {t(
                          "Loan or Self-Arranged Financing options are available",
                        )}
                      </p>

                      <Container tag="div" className="finance-options">
                        {[
                          ["cash", "CASH BUY"],
                          ["loan", "APPLY LOAN"],
                        ].map(([value, label]) => (
                          <button
                            type="button"
                            disabled={readOnly}
                            className={
                              values.buyer_type === value ? "selected" : ""
                            }
                            onClick={() => {
                              setFieldValue("buyer_type", value);
                              setFieldTouched("buyer_type", true, false);
                            }}
                            key={value}
                          >
                            {t(label)}
                          </button>
                        ))}
                      </Container>

                      {touched.buyer_type && errors.buyer_type && (
                        <Container tag="div" className="element _errors">
                          {t(errors.buyer_type)}
                        </Container>
                      )}
                    </fieldset>
                  )}

                  {!readOnly && (
                    <button
                      type="submit"
                      className={`${!values.registration_type ? "mt-4" : ""} purchase-form-submit`}
                      disabled={createIdentityLoading || isSubmitting}
                    >
                      {createIdentityLoading || isSubmitting
                        ? t("Submitting...")
                        : t("Next")}
                    </button>
                  )}
                </Form>
              );
            }}
          </Formik>
        </section>
      }
    />
  );
}
